import { ProductType } from "@b2bportal/core-types";
import { TRIPS_PATH } from "@b2bportal/core-utilities";
import type {
  HotelItinerary,
  PaymentLineItem,
  RewardsAmount,
} from "@b2bportal/lodging-api";
import type { Amount } from "@hopper-b2b/types";
import type { PaymentLineItemValue } from "@/overrides/core-hotels/HotelReservationPage/hooks/useHotelReservationPayment";

export const hotelItineraryPath = (reservationId: string) => `${reservationId}`;

export const hotelItineraryRoute = (reservationId: string) =>
  `${TRIPS_PATH}/${hotelItineraryPath(reservationId)}`;

export const getItineraryRoute = (
  reservationId: string,
  product: ProductType
) => {
  if (product === ProductType.LODGING) {
    return hotelItineraryRoute(reservationId);
  }
  throw new Error(`Product ${product} not supported`);
};

export type FiatPayment = {
  amount: number;
  currency: string;
};

type ReservationPayments = {
  card?: FiatPayment;
  rewards?: number;
};

export type LoyaltyPointsPaymentInfo = {
  pointsAmount?: number;
  fiatAmount?: Amount;
};

/**
 * Extracts loyalty points payment information from either old or new payment format
 */
export const extractLoyaltyPointsPayment = (
  payments: PaymentLineItem[] | PaymentLineItemValue[]
): LoyaltyPointsPaymentInfo => {
  const firstItem = payments[0];

  if (!firstItem) {
    return {};
  }

  // Check if this is the old format (PaymentLineItem[])
  if ("PaymentLineItem" in firstItem) {
    // Old format: payments array with PaymentLineItem
    const pointsPayment = (payments as PaymentLineItem[]).find(
      (payment) => payment.PaymentLineItem === "Rewards"
    );
    return {
      pointsAmount: (pointsPayment?.amount as RewardsAmount)?.rewardsPrice
        ?.value,
      fiatAmount: (pointsPayment?.amount as RewardsAmount)?.fiatValue,
    };
  } else {
    // New format: paymentLineItems array with type discriminator
    const loyaltyPointsItem = (payments as PaymentLineItemValue[]).find(
      (item) => item.type === "LoyaltyPoints"
    );

    if (loyaltyPointsItem?.type === "LoyaltyPoints") {
      const fiatValue = loyaltyPointsItem.value.cashValue.amount;
      return {
        // Convert fiat value back to points (100 points = 1 USD)
        pointsAmount: fiatValue * 100,
        fiatAmount: loyaltyPointsItem.value.cashValue,
      };
    }
  }

  return {};
};

export const getReservationPayments = ({
  reservation,
}: {
  reservation: HotelItinerary;
}): ReservationPayments => {
  const fallbackReservationPayments = {
    card: undefined,
    rewards: undefined,
  };

  if (reservation.paymentBreakdown == null) {
    console.error(
      `paymentBreakdown is missing from reservation ${reservation.reservation.reservationBookingId}`
    );
    return fallbackReservationPayments;
  }

  if (reservation.reservation.pricing.payNowTotal.fiat.value === 0) {
    return fallbackReservationPayments;
  }

  const paymentLineItems = (reservation.paymentBreakdown.paymentLineItems ||
    []) as PaymentLineItemValue[];
  const result: ReservationPayments = { ...fallbackReservationPayments };

  // Get card payment
  const cardPayment = paymentLineItems.find((item) => item.type === "Card");
  if (cardPayment?.type === "Card") {
    result.card = {
      amount: cardPayment.value.paymentAmount.amount,
      currency: cardPayment.value.paymentAmount.currency,
    };
  }

  // Get loyalty points payment using shared helper
  const loyaltyPointsInfo = extractLoyaltyPointsPayment(paymentLineItems);
  if (loyaltyPointsInfo.pointsAmount) {
    result.rewards = loyaltyPointsInfo.pointsAmount;
  }

  return result;
};
